import React, { useState, useRef, ChangeEvent } from 'react';
import {
  User,
  Shield,
  Camera,
  ChevronRight,
  Settings as SettingsIcon,
  Calendar,
  Save,
  Wrench,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api/authApi';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { formatDateWithYear } from '../utils/dateFormat';

interface MenuItem {
  id: string;
  icon: React.ComponentType<{ size?: number }>;
  label: string;
}

interface EditedUser {
  full_name: string;
  bio: string;
}

interface PasswordChangeForm {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

// Animated icon component that transitions between settings and close
const AnimateIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
  return (
    <div className='relative w-5 h-5'>
      <SettingsIcon
        size={20}
        className={`
          absolute transition-all duration-300 ease-out
          ${isOpen ? 'opacity-0 rotate-90 scale-50' : 'opacity-100 rotate-0 scale-100'}
          ${isOpen ? 'text-white' : 'text-gray-600'}
        `}
      />
      <div
        className={`
          absolute inset-0 flex items-center justify-center
          transition-all duration-300 ease-out
          ${isOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-50'}
        `}
      >
        <svg width='20' height='20' viewBox='0 0 20 20' fill='none' className='text-white'>
          <path
            d='M15 5L5 15M5 5l10 10'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          />
        </svg>
      </div>
    </div>
  );
};

const SettingsPage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { t: trans } = useTranslation();
  const [activeSection, setActiveSection] = useState('profile');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadedAvatarUrl, setUploadedAvatarUrl] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isDraggingAvatar, setIsDraggingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editedUser, setEditedUser] = useState<EditedUser>({
    full_name: user?.full_name || '',
    bio: user?.bio || '',
  });

  const [passwordForm, setPasswordForm] = useState<PasswordChangeForm>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const menuItems: MenuItem[] = [
    { id: 'general', icon: Wrench, label: trans('settings.general', 'General') },
    { id: 'profile', icon: User, label: trans('settings.profileSettings') },
    { id: 'security', icon: Shield, label: trans('settings.security') },
  ];

  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedUser(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(trans('settings.avatarInvalidType'));
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error(trans('settings.avatarTooLarge'));
      return;
    }

    setIsUploadingAvatar(true);
    try {
      // Upload avatar to server
      const response = await authAPI.uploadAvatar(file, progress => {
        console.log(`Upload progress: ${progress}%`);
      });

      // Get the base URL from environment and construct full avatar URL
      const avatarUrl = response.data.avatar_url;
      setUploadedAvatarUrl(avatarUrl);

      // Set preview for display
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      toast.success(trans('settings.avatarUploadSuccess'));
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      toast.error(trans('settings.avatarUploadError'));
      // Clear preview on error
      setAvatarPreview(null);
      setUploadedAvatarUrl(null);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Drag and drop handlers for avatar
  const handleAvatarDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploadingAvatar && !isSaving) {
      setIsDraggingAvatar(true);
    }
  };

  const handleAvatarDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAvatar(false);
  };

  const handleAvatarDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAvatar(false);

    if (isUploadingAvatar || isSaving) return;

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(trans('settings.avatarInvalidType'));
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error(trans('settings.avatarTooLarge'));
      return;
    }

    setIsUploadingAvatar(true);
    try {
      // Upload avatar to server
      const response = await authAPI.uploadAvatar(file, progress => {
        console.log(`Upload progress: ${progress}%`);
      });

      // Get the base URL from environment and construct full avatar URL
      const avatarUrl = response.data.avatar_url;
      setUploadedAvatarUrl(avatarUrl);

      // Set preview for display
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      toast.success(trans('settings.avatarUploadSuccess'));
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      toast.error(trans('settings.avatarUploadError'));
      // Clear preview on error
      setAvatarPreview(null);
      setUploadedAvatarUrl(null);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const profileData = {
        full_name: editedUser.full_name,
        bio: editedUser.bio,
        avatar_url: uploadedAvatarUrl || user?.avatar_url || null,
      };
      const response = await authAPI.updateProfile(profileData);
      // Update auth context with new user data
      updateUser(response.data);
      // Clear the uploaded avatar URL after successful save
      setUploadedAvatarUrl(null);
      setAvatarPreview(null);
      toast.success(trans('settings.saveSuccess'));
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(trans('settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (
      !passwordForm.current_password ||
      !passwordForm.new_password ||
      !passwordForm.confirm_password
    ) {
      toast.error(trans('settings.allFieldsRequired'));
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error(trans('settings.passwordsNotMatch'));
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error(trans('settings.passwordTooShort'));
      return;
    }

    setIsChangingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success(trans('settings.passwordChangeSuccess'));
      // Reset form
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(trans('settings.passwordChangeError'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handlePasswordInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const getRoleBadgeColor = (role?: string): string => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'user':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'viewer':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!user) {
    return (
      <div className='flex-1 flex items-center justify-center'>
        <div className='text-center'>
          <User className='mx-auto h-12 w-12 text-gray-400' />
          <h2 className='mt-4 text-lg font-medium text-gray-900'>
            {trans('profile.userNotFound')}
          </h2>
          <p className='mt-2 text-sm text-gray-500'>{trans('profile.pleaseLogIn')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex-1 bg-gray-50 overflow-y-auto'>
      <div className='max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-8 pb-4 sm:pb-8 min-h-full'>
        {/* Elegant Header */}
        <div className='mb-6 sm:mb-8'>
          {/* Desktop Title */}
          <div className='hidden sm:block'>
            <div className='relative'>
              <div className='absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full'></div>
              <h1 className='text-2xl sm:text-3xl font-bold text-gray-900 pl-6'>
                {trans('settings.title')}
              </h1>
            </div>
            <p className='text-gray-500 text-sm mt-2 pl-6'>{trans('settings.subtitle')}</p>
          </div>

          {/* Mobile Header with Integrated Menu */}
          <div className='sm:hidden'>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`
                w-full group relative overflow-hidden rounded-2xl p-4
                bg-white border border-gray-200 shadow-sm
                transition-all duration-300 ease-out
                ${mobileMenuOpen ? 'shadow-lg ring-2 ring-blue-500 ring-opacity-20' : 'hover:shadow-md hover:border-gray-300'}
              `}
            >
              {/* Animated background gradient */}
              <div
                className={`
                absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500
                bg-gradient-to-r from-blue-50 via-transparent to-purple-50
              `}
              ></div>

              {/* Content */}
              <div className='relative flex items-center justify-between'>
                <div className='flex items-center space-x-3'>
                  {/* Dynamic icon container */}
                  <div
                    className={`
                    relative w-10 h-10 rounded-xl flex items-center justify-center
                    transition-all duration-300 ease-out
                    ${
                      mobileMenuOpen
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/30 rotate-90'
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 group-hover:from-blue-100 group-hover:to-blue-200'
                    }
                    }
                  `}
                  >
                    <AnimateIcon isOpen={mobileMenuOpen} />
                  </div>

                  {/* Title with animated underline */}
                  <div className='flex-1'>
                    <div className='flex items-center space-x-2'>
                      <h1 className='text-lg font-bold text-gray-900'>{trans('settings.title')}</h1>
                      {/* Animated indicator dot */}
                      <div
                        className={`
                        w-1.5 h-1.5 rounded-full transition-all duration-300
                        ${mobileMenuOpen ? 'bg-blue-500 scale-100' : 'bg-gray-300 scale-75'}
                      `}
                      ></div>
                    </div>
                    <div
                      className={`
                      text-xs text-gray-500 mt-0.5 transition-all duration-300
                      ${mobileMenuOpen ? 'opacity-100' : 'opacity-60'}
                    `}
                    >
                      {mobileMenuOpen
                        ? trans('settings.tapToClose')
                        : trans('settings.tapToConfigure')}
                    </div>
                  </div>
                </div>

                {/* Chevron indicator */}
                <div
                  className={`
                  transition-transform duration-300 ease-out
                  ${mobileMenuOpen ? 'rotate-180' : 'rotate-0'}
                `}
                >
                  <ChevronRight size={20} className='text-gray-400' />
                </div>
              </div>

              {/* Progress indicator bar */}
              <div
                className={`
                absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500
                transition-all duration-500 ease-out
                ${mobileMenuOpen ? 'w-full' : 'w-0'}
              `}
              ></div>
            </button>
          </div>
        </div>

        <div className='flex flex-col sm:flex-row gap-6'>
          {/* Mobile Menu Bottom Sheet */}
          {mobileMenuOpen && (
            <>
              {/* Backdrop */}
              <div
                className='sm:hidden fixed inset-0 z-[70] bg-black bg-opacity-50 backdrop-blur-sm animate-fade-in'
                onClick={() => setMobileMenuOpen(false)}
              />
              {/* Bottom Sheet */}
              <div
                className='sm:hidden fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-lg z-[80] animate-slide-up-bottom safe-area-bottom'
                onClick={e => e.stopPropagation()}
              >
                {/* Handle bar */}
                <div className='flex justify-center pt-3 pb-2'>
                  <div className='w-12 h-1.5 bg-gray-300 rounded-full'></div>
                </div>
                <div className='p-4 border-b border-gray-200'>
                  <h2 className='text-lg font-semibold text-gray-900'>{trans('settings.menu')}</h2>
                </div>
                <nav className='p-4 space-y-1 pb-8'>
                  {menuItems.map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleSectionChange(item.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                        activeSection === item.id
                          ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <item.icon size={20} />
                      <span className='font-medium text-base'>{item.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </>
          )}

          {/* Sidebar - Desktop */}
          <div className='hidden sm:block w-full sm:w-64 lg:w-72'>
            <nav className='space-y-1'>
              {menuItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center space-x-3 px-3 sm:px-4 py-2 sm:py-3 text-left rounded-lg transition-colors ${
                    activeSection === item.id
                      ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <item.icon size={18} />
                  <span className='font-medium text-sm sm:text-base'>{item.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className='flex-1'>
            <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6'>
              {activeSection === 'profile' && (
                <div>
                  <h2 className='text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6'>
                    {trans('settings.profileSettings')}
                  </h2>

                  {/* Avatar Section */}
                  <div className='flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 mb-8'>
                    <div
                      className='relative'
                      onDragOver={handleAvatarDragOver}
                      onDragLeave={handleAvatarDragLeave}
                      onDrop={handleAvatarDrop}
                    >
                      {/* Drag overlay */}
                      {isDraggingAvatar && (
                        <div className='absolute inset-0 z-10 w-20 h-20 sm:w-24 sm:h-24 bg-blue-50 border-2 border-dashed border-blue-500 rounded-full flex items-center justify-center pointer-events-none'>
                          <Camera size={24} className='text-blue-500' />
                        </div>
                      )}
                      <div
                        className={`w-20 h-20 sm:w-24 sm:h-24 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden transition-all ${
                          isDraggingAvatar ? 'opacity-50' : ''
                        } ${!isUploadingAvatar && !isSaving ? 'cursor-pointer' : ''}`}
                        onClick={() =>
                          !isUploadingAvatar && !isSaving && fileInputRef.current?.click()
                        }
                      >
                        {isUploadingAvatar ? (
                          <div className='flex items-center justify-center'>
                            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
                          </div>
                        ) : avatarPreview || user.avatar_url ? (
                          <img
                            src={avatarPreview || user.avatar_url || ''}
                            alt={user.full_name}
                            className='w-full h-full object-cover'
                          />
                        ) : (
                          <User size={32} className='sm:size-40 text-gray-400' />
                        )}
                      </div>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        disabled={isUploadingAvatar || isSaving}
                        className={`absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                          isUploadingAvatar || isSaving
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                        title={trans('profile.changeAvatar')}
                      >
                        {isUploadingAvatar ? (
                          <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                        ) : (
                          <Camera size={16} className='text-white' />
                        )}
                      </button>
                      <input
                        ref={fileInputRef}
                        type='file'
                        accept='image/*'
                        onChange={handleAvatarChange}
                        disabled={isUploadingAvatar || isSaving}
                        className='hidden'
                      />
                    </div>

                    <div className='text-center sm:text-left flex-1'>
                      <h2 className='text-xl sm:text-2xl font-bold text-gray-900'>
                        {user.full_name}
                      </h2>
                      <p className='text-gray-600 break-all'>{user.email}</p>
                      <div className='mt-2 flex items-center justify-center sm:justify-start space-x-2'>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRoleBadgeColor(user.role)}`}
                        >
                          <Shield size={14} className='mr-1' />
                          {user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Profile Information Form */}
                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6'>
                    <div className='space-y-4'>
                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          {trans('auth.fullName')}
                        </label>
                        <input
                          type='text'
                          name='full_name'
                          value={editedUser.full_name}
                          onChange={handleInputChange}
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                        />
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          {trans('profile.bio')}
                        </label>
                        <textarea
                          name='bio'
                          value={editedUser.bio}
                          onChange={handleInputChange}
                          rows={4}
                          placeholder={trans('settings.bioPlaceholder')}
                          className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                        />
                      </div>
                    </div>

                    <div className='space-y-4'>
                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          {trans('profile.accountType')}
                        </label>
                        <div className='flex items-center space-x-2 text-gray-900'>
                          <Shield size={16} className='text-gray-400 flex-shrink-0' />
                          <span className='capitalize'>{user.role}</span>
                        </div>
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          {trans('profile.memberSince')}
                        </label>
                        <div className='flex items-center space-x-2 text-gray-900'>
                          <Calendar size={16} className='text-gray-400 flex-shrink-0' />
                          <span>{formatDateWithYear(user.created_at) || 'Unknown'}</span>
                        </div>
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          {trans('profile.lastLogin')}
                        </label>
                        <div className='flex items-center space-x-2 text-gray-900'>
                          <Calendar size={16} className='text-gray-400 flex-shrink-0' />
                          <span>{formatDateWithYear(user.last_login) || 'Unknown'}</span>
                        </div>
                      </div>

                      <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                          {trans('profile.accountStatus')}
                        </label>
                        <div className='flex items-center space-x-2'>
                          <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                          <span className='text-gray-900'>{trans('profile.active')}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className='mt-6'>
                    <button
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                      className='flex items-center justify-center space-x-2 px-6 py-2 bg-ui-primary text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      <Save size={18} />
                      <span>
                        {isSaving ? trans('common.loading') : trans('profile.saveChanges')}
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {activeSection === 'security' && (
                <div>
                  <h2 className='text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6'>
                    {trans('settings.securitySettings')}
                  </h2>
                  <form onSubmit={handlePasswordChange} className='space-y-4 sm:space-y-6'>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>
                        {trans('settings.currentPassword')}
                      </label>
                      <input
                        type='password'
                        name='current_password'
                        value={passwordForm.current_password}
                        onChange={handlePasswordInputChange}
                        autoComplete='current-password'
                        className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>
                        {trans('settings.newPassword')}
                      </label>
                      <input
                        type='password'
                        name='new_password'
                        value={passwordForm.new_password}
                        onChange={handlePasswordInputChange}
                        autoComplete='new-password'
                        className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>
                        {trans('settings.confirmNewPassword')}
                      </label>
                      <input
                        type='password'
                        name='confirm_password'
                        value={passwordForm.confirm_password}
                        onChange={handlePasswordInputChange}
                        autoComplete='new-password'
                        className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
                      />
                    </div>
                    <button
                      type='submit'
                      disabled={isChangingPassword}
                      className='w-full sm:w-auto bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      {isChangingPassword
                        ? trans('common.loading')
                        : trans('settings.updatePassword')}
                    </button>
                  </form>
                </div>
              )}

              {activeSection === 'general' && (
                <div>
                  <h2 className='text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6'>
                    {trans('settings.general', 'General')}
                  </h2>
                  <div className='space-y-4 sm:space-y-6'>
                    <LanguageSwitcher />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
